import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Plus, ChevronRight, ChevronDown, Pencil, Trash2, FolderOpen } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Card, Modal, ModalHeader, ModalBody, ModalFooter, Input, EmptyState } from '../../../components/ui'

interface Category {
  id: number
  name: string
  parent_id: number | null
  voters_count: number
  children: Category[]
}

export default function CategoriesPage() {
  const navigate = useNavigate()
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [parentId, setParentId] = useState<number | null>(null)
  const [parentName, setParentName] = useState('')
  const [name, setName] = useState('')
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null)
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})

  useEffect(() => { fetchCategories() }, [])

  async function fetchCategories() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl('/v1/voter-categories'))
      setCategories(res.data.data)
      // Expandir tudo por padrão
      const expandAll: Record<number, boolean> = {}
      function expand(cats: Category[]) {
        cats.forEach(c => { expandAll[c.id] = true; expand(c.children) })
      }
      expand(res.data.data)
      setExpanded(expandAll)
    } catch { /* */ } finally { setLoading(false) }
  }

  function openCreate(parent: Category | null = null) {
    setEditingId(null)
    setParentId(parent?.id || null)
    setParentName(parent?.name || '')
    setName('')
    setModalOpen(true)
  }

  function openEdit(cat: Category) {
    setEditingId(cat.id)
    setParentId(cat.parent_id)
    setParentName('')
    setName(cat.name)
    setModalOpen(true)
  }

  function openDelete(cat: Category) {
    setDeletingCategory(cat)
    setDeleteModalOpen(true)
  }

  async function handleSave() {
    if (!name.trim() || saving) return
    setSaving(true)
    try {
      if (editingId) {
        await axios.put(buildApiUrl(`/v1/voter-categories/${editingId}`), { name })
        toast('success', 'Categoria atualizada')
      } else {
        await axios.post(buildApiUrl('/v1/voter-categories'), { name, parent_id: parentId })
        toast('success', 'Categoria criada')
      }
      setModalOpen(false)
      fetchCategories()
    } catch (err: any) {
      toast('error', err.response?.data?.error || 'Erro ao salvar')
    } finally { setSaving(false) }
  }

  async function handleDelete() {
    if (!deletingCategory) return
    try {
      await axios.delete(buildApiUrl(`/v1/voter-categories/${deletingCategory.id}`))
      toast('success', 'Categoria desativada')
      setDeleteModalOpen(false)
      fetchCategories()
    } catch {
      toast('error', 'Erro ao desativar')
    }
  }

  function toggleExpand(id: number) {
    setExpanded(e => ({ ...e, [id]: !e[id] }))
  }

  function countTotal(cat: Category): number {
    return cat.voters_count + cat.children.reduce((sum, c) => sum + countTotal(c), 0)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/settings/general')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Categorias</h1>
            <p className="text-gray-500 mt-1">Organize contatos e eleitores por categorias</p>
          </div>
        </div>
        <Button onClick={() => openCreate()}>
          <Plus className="w-4 h-4 mr-1.5" />Nova categoria
        </Button>
      </div>

      {/* Árvore */}
      <Card>
        {loading ? (
          <div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse" />)}</div>
        ) : categories.length === 0 ? (
          <EmptyState
            icon={<FolderOpen className="w-7 h-7" />}
            title="Nenhuma categoria"
            description="Crie categorias para organizar seus contatos e eleitores em grupos."
            action={<Button size="sm" onClick={() => openCreate()}><Plus className="w-4 h-4 mr-1" />Nova categoria</Button>}
          />
        ) : (
          <div className="space-y-0.5">
            {categories.map(cat => (
              <CategoryNode
                key={cat.id}
                category={cat}
                level={0}
                expanded={expanded}
                onToggle={toggleExpand}
                onAddChild={openCreate}
                onEdit={openEdit}
                onDelete={openDelete}
                countTotal={countTotal}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Modal criar/editar */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setModalOpen(false)}>
          {editingId ? 'Editar categoria' : parentId ? `Nova subcategoria de "${parentName}"` : 'Nova categoria'}
        </ModalHeader>
        <ModalBody>
          <Input
            label="Nome da categoria"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Ex: Apoiadores, Profissionais de Saúde..."
            autoFocus
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={!name.trim()} loading={saving}>
            {editingId ? 'Salvar' : 'Criar'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal excluir */}
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setDeleteModalOpen(false)}>Desativar categoria</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-600">
            Deseja desativar <strong>{deletingCategory?.name}</strong>?
            {deletingCategory && deletingCategory.children.length > 0 && (
              <span className="text-warning-500"> Todas as subcategorias também serão desativadas.</span>
            )}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleDelete}>Desativar</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

function CategoryNode({
  category, level, expanded, onToggle, onAddChild, onEdit, onDelete, countTotal,
}: {
  category: Category; level: number; expanded: Record<number, boolean>
  onToggle: (id: number) => void; onAddChild: (cat: Category) => void
  onEdit: (cat: Category) => void; onDelete: (cat: Category) => void
  countTotal: (cat: Category) => number
}) {
  const hasChildren = category.children.length > 0
  const isExpanded = expanded[category.id]
  const total = countTotal(category)

  return (
    <div>
      <div
        className={clsx(
          'flex items-center gap-2 py-2.5 px-3 rounded-lg hover:bg-gray-50 transition-colors group',
          level > 0 && 'ml-6'
        )}
      >
        {/* Expand/collapse */}
        <button
          onClick={() => hasChildren && onToggle(category.id)}
          className={clsx('w-5 h-5 flex items-center justify-center rounded text-gray-400', hasChildren ? 'hover:text-gray-600 cursor-pointer' : 'invisible')}
        >
          {hasChildren && (isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
        </button>

        {/* Nome */}
        <span className="text-sm font-medium text-gray-800 flex-1">{category.name}</span>

        {/* Contagem */}
        {total > 0 && (
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{total}</span>
        )}

        {/* Ações */}
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onAddChild(category)} className="h-7 w-7 flex items-center justify-center rounded text-gray-400 hover:text-brand-500 hover:bg-brand-50 transition-colors" title="Adicionar subcategoria">
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onEdit(category)} className="h-7 w-7 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title="Editar">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(category)} className="h-7 w-7 flex items-center justify-center rounded text-gray-400 hover:text-error-500 hover:bg-error-50 transition-colors" title="Desativar">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Filhos */}
      {hasChildren && isExpanded && (
        <div className="border-l border-gray-100 ml-5">
          {category.children.map(child => (
            <CategoryNode
              key={child.id}
              category={child}
              level={level + 1}
              expanded={expanded}
              onToggle={onToggle}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              countTotal={countTotal}
            />
          ))}
        </div>
      )}
    </div>
  )
}
